import {
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { getMimeTypes } from '@common/utils/file'
import type { ColumnDef, ExpandedState, Row } from '@tanstack/react-table'
import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router-dom'
import XLSX from 'xlsx'

import RightChevronIcon from '@/assets/icons/icon-chevron-right.svg'
import CopyIcon from '@/assets/icons/icon-copy.svg'
import FolderIcon from '@/assets/icons/icon-folder.svg'
import NewFolderIcon from '@/assets/icons/icon-new-folder.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Export } from '@/components/ui/export'
import { ImportFile } from '@/components/ui/import'
import { SearchInput } from '@/components/ui/search-input.tsx'
import { TreeTable } from '@/components/ui/table/expandable-dnd-table/TreeTable'
import type { Entry } from '@/components/ui/table/expandable-dnd-table/useTreeTable'
import { useTreeTable } from '@/components/ui/table/expandable-dnd-table/useTreeTable'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $currentCreator } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { moveScriptsCategoryFx } from '@/models/scripts/categories.ts'
import {
  $scriptCategories,
  $scriptsError,
  $scriptsExpanded,
  getScriptsFx,
  importScriptsFx,
  moveScriptFx,
  setScriptCategories,
  setScriptsExpansion,
} from '@/models/scripts/scripts.ts'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { ActionButtons } from '@/modules/scripts/components/ActionButtons'
import { Skeleton } from '@/modules/scripts/components/Skeleton'
import type { ScriptNode } from '@/modules/scripts/types'
import { AppRoutes } from '@/utils/constants.ts'
import { useCreatorUrl } from '@/utils/creators.ts'
import { getErrorMessage } from '@/utils/error'
import { exportDataFromServer } from '@/utils/export/export'
import { classNames } from '@/utils/helpers'
import { useDebouncedCallback } from '@/utils/useDebouncedCallback'

export type SelectedItemsMap = Map<
  ScriptNode['id'],
  Map<ScriptNode['id'], ScriptNode>
>

const acceptedSheetType = getMimeTypes({
  types: ['sheet'],
  subTypes: { sheet: ['csv'] },
}).join(',')

export const ScriptsView = () => {
  const data = useUnit($scriptCategories)
  const isFetching = useUnit(getScriptsFx.pending)
  const scriptsError = useUnit($scriptsError)
  const scriptsExpanded = useUnit($scriptsExpanded)
  const [selectedItems, setSelectedItems] = useState<SelectedItemsMap>(
    new Map(),
  )

  useEffect(() => {
    setSelectedItems(new Map())
  }, [data])

  const activeCreator = useUnit($currentCreator)

  const columns = useMemo<ColumnDef<Entry<ScriptNode>>[]>(
    () => [
      {
        enableSorting: true,
        header: 'Folder Name',
        accessorKey: 'name',
        sortingFn: (a, b) => a.original.name.localeCompare(b.original.name),
        cell: ({ row, getValue }) => (
          <div className="flex select-none items-center gap-2.5">
            {row.getCanExpand() && (
              <IconButton
                icon={RightChevronIcon}
                iconClassName={classNames(
                  'transform text-title transition-all duration-300 ease-out',
                  row.getIsExpanded() && 'rotate-90',
                )}
                className="text-title"
                testId="script-expand-button"
                onClick={row.getToggleExpandedHandler()}
              />
            )}
            {row.original._type === 'folder' && <FolderIcon />}
            <div
              className="max-w-[300px] overflow-hidden text-ellipsis"
              data-testid="script-name"
            >
              {getValue<ReactNode>()}
            </div>
          </div>
        ),
      },
      {
        id: 'actions',
        header: () => 'Actions',
        cell: ({ row }) => (
          <ActionButtons
            row={row}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        ),
      },
    ],
    [selectedItems, setSelectedItems],
  )

  const handleExpandedChange = useCallback(
    (callbackOrValue: SetStateAction<ExpandedState>) => {
      const updatedState =
        typeof callbackOrValue === 'function'
          ? callbackOrValue(scriptsExpanded)
          : callbackOrValue

      setScriptsExpansion(updatedState)
    },
    [scriptsExpanded],
  )

  const table = useTreeTable<ScriptNode>({
    data,
    columns,
    expanded: scriptsExpanded,
    onExpandedChange: handleExpandedChange,
    enableSorting: true,
  })

  const move = useCallback(
    (root: Entry<ScriptNode>[], row: Row<Entry<ScriptNode>>) => {
      const isDraggingCategory = row.original._type === 'folder'

      if (isDraggingCategory) {
        const orderedTable = root.map((category) => category.id)

        moveScriptsCategoryFx(orderedTable)

        return
      }
      const updatedOrders: Record<number, number[]> = {}

      for (const folder of root) {
        if (folder._type !== 'folder') {
          continue
        }
        const children = folder?._listChildren?.() ?? []

        updatedOrders[folder.id] = children.map((script) => script.id)
      }
      moveScriptFx({ updatedOrders, id: row.original.id })
    },
    [],
  )

  const columnToFilterBy = table
    .getHeaderGroups()
    .map((group) => group.headers[0])[0].column

  const navigate = useNavigate()

  const createUrl = useCreatorUrl(AppRoutes.Creator.Settings.Scripts.Create)

  const handleNewScriptPress = useCallback(() => {
    navigate(createUrl)
  }, [createUrl, navigate])

  const handleFolderPress = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.CREATE_SCRIPTS_FOLDER,
    })
  }, [])
  const handleCopyScriptClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.COPY_SCRIPT,
      scriptsMap: selectedItems,
      onResetRelectedScripts: () => setSelectedItems(new Map()),
    })
  }, [selectedItems])

  const handleChange = useCallback(
    (callbackOrValue: SetStateAction<Entry<ScriptNode>[]>) => {
      const updatedState =
        typeof callbackOrValue === 'function'
          ? callbackOrValue(data)
          : callbackOrValue

      setScriptCategories(updatedState)
    },
    [data],
  )

  const onFilterUpdate = useDebouncedCallback(
    500,
    (value: string) => {
      table.toggleAllRowsExpanded(!!value)
      columnToFilterBy.setFilterValue(value)
    },
    [columnToFilterBy],
  )

  if ((isFetching && !data.length) || scriptsError) {
    return <Skeleton />
  }

  const downloadSheet = () => {
    if (!activeCreator) return Promise.resolve()

    return exportDataFromServer(activeCreator.id, {
      type: 'Scripts',
      filename: `Scripts`,
      fields: {},
    })
  }

  const handleUpload = async (file: File) => {
    return new Promise<void>((resolve, reject) => {
      if (!activeCreator) return reject('No active creator')
      if (!file.name.endsWith('.csv')) return reject('Invalid file type')
      if (file.size > 3000000) return reject('File size must be less than 3MB')

      const reader = new FileReader()

      reader.readAsText(file)

      reader.onload = async () => {
        const report = XLSX.read(reader.result, {
          type: 'string',
          raw: true,
          cellText: true,
        })
        const worksheet = report.Sheets[report.SheetNames[0]]
        const data = XLSX.utils.sheet_to_json(worksheet) as any[]

        try {
          const result = await importScriptsFx(data)

          resolve(result)
        } catch (error) {
          reject(getErrorMessage(error))
        }
      }
    })
  }

  const selectedItemsCount =
    selectedItems.size +
    [...selectedItems.values()].reduce((acc, curr) => acc + curr.size, 0)

  const isExportAvailable =
    !!data?.length && data.some((d) => !!d.scripts?.length)

  return (
    <>
      <Page
        title="Scripts"
        className="relative"
        description={
          <span className="flex items-center gap-1">
            Send out pre-written messages to your fans
          </span>
        }
        headerOptions={
          <div className="grid grid-cols-12 gap-2 xl:items-end">
            <Export
              getFromServer={downloadSheet}
              className="col-span-4"
              disabled={!isExportAvailable}
              testId="export-scripts-button"
            />
            <SearchInput
              placeholder="Search"
              onInputChange={onFilterUpdate}
              className="col-span-8 w-full pl-4"
              testId="scripts-search-input"
            />
            <ImportFile
              accept={acceptedSheetType}
              handleUpload={handleUpload}
              className="col-span-4"
              testId="import-scripts-button"
            />
            <Button
              variant="outline"
              onClick={handleFolderPress}
              className="col-span-4"
              testId="new-folder-button"
            >
              <NewFolderIcon />
              Create Folder
            </Button>
            <Button
              onClick={handleNewScriptPress}
              className="col-span-4 gap-1"
              testId="new-script-button"
            >
              <PlusIcon />
              New Script
            </Button>
          </div>
        }
      >
        <TreeTable
          key={selectedItemsCount}
          table={table}
          columns={columns}
          onMove={move}
          expanded={scriptsExpanded}
          onChange={handleChange}
          onExpandedChange={handleExpandedChange}
          preventFolderNesting
          groupUncategorized
          enableSorting
        />
      </Page>
      {!!selectedItemsCount && (
        <Button
          className="fixed bottom-10 left-1/2 -translate-x-1/2 lg:left-[calc(150px+50%)]"
          onClick={handleCopyScriptClick}
        >
          <CopyIcon className="mr-2 inline-block" />
          Copy to other Creators
        </Button>
      )}
    </>
  )
}
